import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import { ApiResponse } from '../../types/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import ImportModal from '../../components/Common/ImportModal';
import { motoristasImportConfig } from '../../config/importConfigs';

interface Driver {
  id: number;
  nome: string;
  cpf: string;
  cnh: string;
  validade_cnh: string;
  telefone: string;
  email?: string;
  endereco?: string;
  status: string;
}

const Drivers: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: drivers,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Driver>({
    fetchFunction: async (page, limit, filters) => {
      try {
        // O backend grava status em minusculo; convertemos o filtro (Capitalizado na UI)
        const statusParaBackend: Record<string, string> = { 'Ativo': 'ativo', 'Inativo': 'inativo' };
        const response = await apiService.getDrivers({
          page,
          limit,
          status: filters?.status ? (statusParaBackend[filters.status] || filters.status) : undefined
        });

        if (response.success && response.data) {
          const apiDrivers = Array.isArray(response.data) ? response.data : (response.data as any).items || [];

          const convertedDrivers: Driver[] = apiDrivers.map((driver: any) => ({
            id: driver.id,
            nome: driver.nome,
            cpf: driver.cpf,
            cnh: driver.cnh_numero || driver.cnh,
            validade_cnh: driver.cnh_vencimento || driver.validade_cnh,
            telefone: driver.telefone || driver.celular,
            email: driver.email,
            endereco: driver.endereco_rua ?
              `${driver.endereco_rua}${driver.endereco_numero ? ', ' + driver.endereco_numero : ''}${driver.endereco_complemento ? ' - ' + driver.endereco_complemento : ''}${driver.endereco_bairro ? ' - ' + driver.endereco_bairro : ''}${driver.endereco_cidade ? ' - ' + driver.endereco_cidade : ''}${driver.endereco_estado ? '/' + driver.endereco_estado : ''}${driver.endereco_cep ? ' - CEP: ' + driver.endereco_cep : ''}`
              : undefined,
            status: driver.status === 'ativo' ? 'Ativo' : 'Inativo'
          }));

          // A paginação está no nível root do response, não dentro de data
          return {
            success: true,
            data: {
              items: convertedDrivers,
              pagination: {
                total: (response as any).pagination?.total || convertedDrivers.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar motoristas:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar motoristas'
        };
      }
    },
    itemsPerPage: 10,
    filters: { status: statusFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Ativo': 'status-active',
      'Inativo': 'status-inactive'
    };
    return statusMap[status] || 'status-pending';
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone?: string): string => {
    // telefone/celular podem vir nulos (registros legados); evita crash de .replace em undefined
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = (driver.nome || '').toLowerCase().includes(filter.toLowerCase()) ||
                         (driver.cpf || '').includes(filter) ||
                         (driver.cnh || '').includes(filter);
    const matchesStatus = !statusFilter || driver.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const viewDriver = (id: number) => {
    navigate(`/motoristas/visualizar/${id}`);
  };

  const editDriver = (id: number) => {
    navigate(`/motoristas/editar/${id}`);
  };

  const deleteDriver = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este motorista?')) {
      try {
        const response = await apiService.delete(`DRIVER_BY_ID`, { id }) as ApiResponse<{ message: string }>;
        if (response.success) {
          utils.showNotification('Motorista excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erro ao excluir motorista');
        }
      } catch (error) {
        console.error('Erro ao excluir motorista:', error);
        utils.showNotification('Erro ao excluir motorista', { type: 'error' });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Motoristas</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Motorista
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando motoristas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Motoristas</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.MOTORISTAS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.MOTORISTAS) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/motoristas/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Motorista
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Cadastro de Motoristas</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Nome, CPF ou CNH...'
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'Ativo', label: 'Ativo' },
                    { value: 'Inativo', label: 'Inativo' }
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Motoristas"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredDrivers.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="motoristas"
              />
            )}

            {filteredDrivers.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-users empty-state-icon"></i>
                <h3>Nenhum motorista encontrado</h3>
                <p>{filter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros motoristas da sua equipe.'}</p>
                {canCreate(SystemModule.MOTORISTAS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/motoristas/novo')}
                  >
                    Cadastrar Primeiro Motorista
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>CPF</th>
                      <th>CNH</th>
                      <th>Validade CNH</th>
                      <th>Telefone</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers.map(driver => (
                      <tr key={driver.id}>
                        <td>
                          <strong>{driver.nome}</strong>
                          {driver.endereco && (
                            <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                              {driver.endereco}
                            </div>
                          )}
                        </td>
                        <td>{driver.cpf}</td>
                        <td>
                          <span className="status-badge status-info">
                            {driver.cnh}
                          </span>
                        </td>
                        <td>{formatDate(driver.validade_cnh)}</td>
                        <td>{formatPhone(driver.telefone)}</td>
                        <td>
                          {driver.email ? (
                            <a href={`mailto:${driver.email}`} style={{ color: '#04C64F' }}>
                              {driver.email}
                            </a>
                          ) : '-'}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(driver.status)}`}>
                            {driver.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewDriver(driver.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.MOTORISTAS) && (
                              <button
                                className="btn-edit"
                                onClick={() => editDriver(driver.id)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.MOTORISTAS) && (
                              <button
                                className="btn-delete"
                                onClick={() => deleteDriver(driver.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos motoristas */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-value">
              {drivers.filter(d => d.status === 'Ativo').length}
            </div>
            <div className="stat-label">Motoristas Ativos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-user-slash"></i>
            </div>
            <div className="stat-value">
              {drivers.filter(d => d.status === 'Inativo').length}
            </div>
            <div className="stat-label">Motoristas Inativos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-id-card"></i>
            </div>
            <div className="stat-value">
              {drivers.filter(d => new Date(d.validade_cnh) > new Date()).length}
            </div>
            <div className="stat-label">CNHs Válidas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-value">
              {drivers.filter(d => new Date(d.validade_cnh) < new Date()).length}
            </div>
            <div className="stat-label">CNHs Vencidas</div>
          </div>
        </div>

        {/* Informações importantes */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações Importantes</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div>
                <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>
                  <i className="fas fa-exclamation-circle" style={{ color: '#e74c3c', marginRight: '8px' }}></i>
                  CNHs Próximas do Vencimento
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {drivers
                    .filter(d => {
                      const validityDate = new Date(d.validade_cnh);
                      const today = new Date();
                      const diffTime = validityDate.getTime() - today.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return diffDays > 0 && diffDays <= 30;
                    })
                    .map(driver => (
                      <li key={driver.id}>
                        <strong>{driver.nome}</strong> - Vence em {formatDate(driver.validade_cnh)}
                      </li>
                    ))
                  }
                  {drivers.filter(d => {
                    const validityDate = new Date(d.validade_cnh);
                    const today = new Date();
                    const diffTime = validityDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays > 0 && diffDays <= 30;
                  }).length === 0 && (
                    <li>Nenhuma CNH próxima do vencimento</li>
                  )}
                </ul>
              </div>

              <div>
                <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>
                  <i className="fas fa-ban" style={{ color: '#e74c3c', marginRight: '8px' }}></i>
                  CNHs Vencidas
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {drivers
                    .filter(d => new Date(d.validade_cnh) < new Date())
                    .map(driver => (
                      <li key={driver.id}>
                        <strong>{driver.nome}</strong> - Venceu em {formatDate(driver.validade_cnh)}
                      </li>
                    ))
                  }
                  {drivers.filter(d => new Date(d.validade_cnh) < new Date()).length === 0 && (
                    <li>Nenhuma CNH vencida</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={motoristasImportConfig}
      />
    </div>
  );
};

export default Drivers;
